import { add, cross, dot, normalise, scale } from '../geo/vec3.js';
/**
 * A gnomonic (central) projection about a chosen centre.
 *
 * Great circles project to straight lines, which is the property the partition
 * engine needs: a spherical power diagram becomes an ordinary planar Laguerre
 * diagram whose cells are convex polygons that tile exactly. The projection
 * diverges at 90° from the centre, so it is only ever used within one
 * continent, which is far smaller than that.
 */
export class Gnomonic {
    centre;
    east;
    north;
    constructor(centre) {
        this.centre = normalise(centre);
        const seed = Math.abs(this.centre.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
        this.east = normalise(cross(this.centre, seed));
        this.north = normalise(cross(this.centre, this.east));
    }
    /** Returns null for points on or beyond the horizon, where the projection blows up. */
    project(v) {
        const forward = dot(v, this.centre);
        if (forward <= 1e-6)
            return null;
        return { x: dot(v, this.east) / forward, y: dot(v, this.north) / forward };
    }
    unproject(p) {
        return normalise(add(this.centre, add(scale(this.east, p.x), scale(this.north, p.y))));
    }
    projectRing(ring) {
        const out = [];
        for (const v of ring) {
            const p = this.project(v);
            if (!p)
                return null;
            out.push(p);
        }
        return out;
    }
    unprojectRing(ring) {
        return ring.map((p) => this.unproject(p));
    }
}
/** Sutherland-Hodgman in the plane: exact, and convex clips keep it well behaved. */
export function clipByHalfPlane(ring, half) {
    if (ring.length === 0)
        return [];
    const distance = (p) => half.a * p.x + half.b * p.y - half.c;
    const out = [];
    for (let i = 0; i < ring.length; i += 1) {
        const current = ring[i];
        const next = ring[(i + 1) % ring.length];
        const dCurrent = distance(current);
        const dNext = distance(next);
        if (dCurrent <= 0)
            out.push(current);
        if ((dCurrent > 0 && dNext < 0) || (dCurrent < 0 && dNext > 0)) {
            const t = dCurrent / (dCurrent - dNext);
            out.push({
                x: current.x + (next.x - current.x) * t,
                y: current.y + (next.y - current.y) * t,
            });
        }
    }
    return out;
}
export function clipByHalfPlanes(ring, halves) {
    let current = [...ring];
    for (const half of halves) {
        current = clipByHalfPlane(current, half);
        if (current.length < 3)
            return [];
    }
    return current;
}
/**
 * The Laguerre (power) bisector between two weighted sites: the set where
 * |x-a|² - wa = |x-b|² - wb. Always a straight line, whatever the weights,
 * which is exactly what the spherical formulation could not guarantee.
 */
export function powerBisector(a, wa, b, wb) {
    return {
        a: 2 * (b.x - a.x),
        b: 2 * (b.y - a.y),
        c: b.x * b.x + b.y * b.y - wb - (a.x * a.x + a.y * a.y - wa),
    };
}
export function centroid2(ring) {
    if (ring.length === 0)
        return { x: 0, y: 0 };
    let area = 0;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < ring.length; i += 1) {
        const p = ring[i];
        const q = ring[(i + 1) % ring.length];
        const twice = p.x * q.y - q.x * p.y;
        area += twice;
        cx += (p.x + q.x) * twice;
        cy += (p.y + q.y) * twice;
    }
    if (Math.abs(area) < 1e-15) {
        const sum = ring.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
        return { x: sum.x / ring.length, y: sum.y / ring.length };
    }
    return { x: cx / (3 * area), y: cy / (3 * area) };
}
/** Standard even-odd test, in the projected plane. */
export function contains2(ring, p) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const a = ring[i];
        const b = ring[j];
        const straddles = a.y > p.y !== b.y > p.y;
        if (straddles && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)
            inside = !inside;
    }
    return inside;
}
export function bounds2(ring) {
    return {
        minX: Math.min(...ring.map((p) => p.x)),
        maxX: Math.max(...ring.map((p) => p.x)),
        minY: Math.min(...ring.map((p) => p.y)),
        maxY: Math.max(...ring.map((p) => p.y)),
    };
}
//# sourceMappingURL=gnomonic.js.map